import { getRbacMatchNames } from './permissions';

const nameKey = value => String(value || '').trim().toLowerCase();

export function createRepResolver(users) {
  const displayNames = new Map();
  const aliases = new Map();
  const byUid = new Map();
  users.forEach(user => {
    const displayName = user.displayName?.trim();
    if (!displayName) return;
    displayNames.set(nameKey(displayName), displayName);
    [user.uid, user.user_id, user.salesRepId].filter(value => value != null)
      .forEach(value => byUid.set(String(value), displayName));
    getRbacMatchNames(user).forEach(alias => {
      if (!aliases.has(alias)) aliases.set(alias, new Set());
      aliases.get(alias).add(displayName);
    });
  });
  const resolve = name => {
    const key = nameKey(name);
    if (displayNames.has(key)) return displayNames.get(key);
    const matches = aliases.get(key);
    return matches?.size === 1 ? [...matches][0] : String(name || '').trim();
  };
  const opportunityRep = opportunity =>
    byUid.get(String(opportunity.uid)) || resolve(opportunity.salesRepName);
  const matches = (names, selected) => {
    const keys = new Set(selected.map(name => nameKey(resolve(name))));
    return names.some(name => keys.has(nameKey(resolve(name))));
  };
  const options = names => {
    const hidden = new Set(users.filter(u => u.hideFromReports).map(u => nameKey(resolve(u.displayName))));
    const unique = new Map();
    names.forEach(name => {
      const resolved = resolve(name);
      if (resolved && !hidden.has(nameKey(resolved))) unique.set(nameKey(resolved), resolved);
    });
    return [...unique.values()].sort((a, b) => a.localeCompare(b)).map(name => ({ name }));
  };
  return { resolve, opportunityRep, matches, options };
}

export function createCustomerLookup(customers) {
  const accounts = new Map();
  const internalIds = new Map();
  customers.forEach(customer => {
    [customer.id, customer.customerAccountNumber, customer.customerId].filter(value => value != null)
      .forEach(value => accounts.set(String(value), customer));
    if (customer.apparelMagicId != null) internalIds.set(String(customer.apparelMagicId), customer);
  });
  return {
    forAccount: id => accounts.get(String(id)),
    forOrder: order => accounts.get(String(order.customerAccountNumber)) ||
      internalIds.get(String(order.customer_id)),
  };
}

export function isAssignedCustomer(customer, user) {
  const names = new Set(getRbacMatchNames(user));
  return Boolean(customer?.salesRepNames?.some(name => names.has(nameKey(name))));
}

export function visibleCustomerOrders(orders, customers, user, viewAll) {
  if (viewAll) return orders;
  const lookup = createCustomerLookup(customers);
  return orders.filter(order => isAssignedCustomer(lookup.forOrder(order), user));
}

export function currentInactivityRows(rows, customers, user, viewAll) {
  const lookup = createCustomerLookup(customers);
  return rows.flatMap(row => {
    const customer = lookup.forAccount(row.customerAccountNumber ?? row.id);
    if (!customer || !['1', 1, true].includes(customer.isActive) ||
        ['cancelled', 'declined'].includes(nameKey(customer.customerStatus))) return [];
    if (!viewAll && !isAssignedCustomer(customer, user)) return [];
    return [{
      ...row,
      id: customer.id,
      customerName: customer.customerName,
      customerStatus: customer.customerStatus,
      salesRepNames: customer.salesRepNames || [],
    }];
  });
}
